"use client";

import * as React from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from "@focal/ui-kit";
import { ConversationPersonTagSearchItem } from "@/components/pages/Conversations/ConversationsDetails/ConversationPersonTag/ConversationPersonTagSearchItem";
import {
  PopoverContent,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@focal/ui-kit";
import { Client, Participant, User, SuggestedParticipants } from "@/types";
import {
  getParticipantEmail,
  getParticipantName,
} from "@/lib/participant-utils";
import { useQuery, useMutation } from "@tanstack/react-query";
import { USERS_ALL_GET, getAllClients, getAllUsers } from "@/api";
import { useDebounce } from "@/hooks";
import { useDefaultClients } from "@/hooks/useDefaultClients";
import { LoadingSpinner, Text } from "@focal/ui-kit";
import { useCallback, useEffect, useMemo } from "react";
import { ClientModal } from "@/components/pages/Clients/components";
import { Button } from "@focal/ui-kit";
import { Icon } from "@focal/ui-kit";
import { useParams } from "next/navigation";
import { useCrm } from "@/components/pages/Settings/integrations/hooks";
import { SuggestedParticipantsSection } from "./SuggestedParticipantsSection";
import { useAnalytics, getPrimaryEmail } from "@/lib";
import { ANALYTICS_EVENTS } from "@/constants";
import { useUnclaimedSuggestions } from "./hooks";
import { useTranscriptForcePortal } from "@/components/pages/Conversations/ConversationsDetails/ConversationTranscript/TranscriptPopoverPortalContext";
import { cn } from "@/lib/utils";

const MIN_SEARCH_LENGTH = 3;
const MAX_CLIENT_RESULTS_PER_TYPE = 40;
const MAX_USER_RESULTS_PER_TYPE = 10;
const MAX_DEFAULT_CLIENTS = 10;
const LOADING_TRANSITION_DELAY = 150;

type AuthorType = "client" | "user";

export type UpdateParticipantData = {
  data: Client | User;
  type: AuthorType;
  participantId?: string;
  speakerId?: string | null;
};

const toUpdateParticipantData = (
  p: Partial<Participant>,
  currentParticipant?: Partial<Participant>,
): UpdateParticipantData => {
  const source = p.userId ? p.user : p.client;
  const nameParts = (p.name || "").split(" ");
  const fallbackFirst = nameParts[0] || "";
  const fallbackLast = nameParts.slice(1).join(" ") || "";
  const firstname = source?.firstname || fallbackFirst;
  const lastname = source?.lastname || fallbackLast;
  return {
    data: { id: p.clientId || p.userId, firstname, lastname } as Client,
    type: p.userId ? "user" : "client",
    participantId: p.id,
    speakerId: currentParticipant?.speakerId,
  };
};

interface ConversationPersonPopoverProps {
  participant?: Partial<Participant>;
  onSelectParticipant: (data: UpdateParticipantData) => void;
  onCloseModal: () => void;
  open: boolean;
  disableUsers?: boolean;
  participants?: Partial<Participant>[];
  suggestedParticipants?: SuggestedParticipants;
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "center" | "end";
  // SpeakerRow-specific sections
  onUnassign?: () => void;
  existingParticipants?: Partial<Participant>[];
  // Customization
  placeholder?: string;
  popoverClassName?: string;
  avoidCollisions?: boolean;
  forcePortal?: boolean;
  /** Pre-meeting flows have no conversation route — pass explicitly (or omit on conversation pages, where it falls back to the route param). */
  conversationId?: string;
  suggestedHeading?: string;
  /** Cap forwarded to SuggestedParticipantsSection — see its maxVisible prop. */
  suggestedMaxVisible?: number;
  createClientLabel?: string;
}

export const ConversationPersonPopoverPaginated = ({
  participant,
  onCloseModal,
  onSelectParticipant,
  open,
  disableUsers = false,
  participants,
  suggestedParticipants,
  side = "bottom",
  align = "start",
  onUnassign,
  existingParticipants,
  placeholder = "Search participants...",
  popoverClassName,
  avoidCollisions,
  forcePortal: forcePortalProp,
  conversationId: conversationIdProp,
  suggestedHeading,
  suggestedMaxVisible,
  createClientLabel = "Create client",
}: ConversationPersonPopoverProps) => {
  const forcePortalFromContext = useTranscriptForcePortal();
  const forcePortal = forcePortalProp || forcePortalFromContext;
  const { id: routeConversationId } = useParams<{ id?: string }>();
  const conversationId = conversationIdProp ?? routeConversationId;
  const { track } = useAnalytics();
  const [search, setSearch] = React.useState("");
  const debouncedSearch = useDebounce(search, 500);
  const isTypedNotEnough = debouncedSearch.length < MIN_SEARCH_LENGTH;

  const { excludedClientIds, excludedUserIds } = useUnclaimedSuggestions(
    suggestedParticipants,
    participants,
  );

  // Fetch all users (no pagination) — gated on open to avoid eager prefetch
  const { data: users, isLoading: isLoadingUsers } = useQuery({
    queryKey: [USERS_ALL_GET],
    queryFn: () => getAllUsers(),
    enabled: !disableUsers && open,
  });

  const { data: defaultClients, isLoading: isLoadingDefaultClients } =
    useDefaultClients({ enabled: open });

  // Track the search term that the current clients data corresponds to
  const [lastFetchedSearch, setLastFetchedSearch] = React.useState("");

  // Fetch clients on demand (paginated at API level)
  const {
    mutate: fetchClients,
    data: clients,
    isPending: isLoadingClients,
  } = useMutation({
    mutationFn: () =>
      getAllClients({
        search: debouncedSearch,
        take: MAX_CLIENT_RESULTS_PER_TYPE,
      }),
    onSuccess: () => {
      setLastFetchedSearch(debouncedSearch);
    },
  });

  // Trigger fetchClients only when search is valid
  React.useEffect(() => {
    if (debouncedSearch.length >= MIN_SEARCH_LENGTH) {
      fetchClients();
    }
  }, [debouncedSearch, fetchClients]);

  // Filter users locally
  const filteredUsers = useMemo(() => {
    if (!users || debouncedSearch.length < MIN_SEARCH_LENGTH) return [];
    const excludedUserIdSet = new Set(excludedUserIds);

    if (!participants || !Array.isArray(participants)) {
      return users
        .filter(
          (user) =>
            `${user.firstname} ${user.lastname}`
              .toLowerCase()
              .includes(debouncedSearch.toLowerCase()) &&
            !excludedUserIdSet.has(user.id),
        )
        .slice(0, MAX_USER_RESULTS_PER_TYPE);
    } else {
      const participantsUserIds = participants
        .map((p) => p.userId)
        .filter(Boolean);

      return users
        .filter((user) => {
          const fullName = `${user.firstname} ${user.lastname}`.toLowerCase();
          const isParticipant = participantsUserIds.includes(user.id);
          const isSuggested = excludedUserIdSet.has(user.id);
          return (
            fullName.includes(debouncedSearch.toLowerCase()) &&
            !isParticipant &&
            !isSuggested
          );
        })
        .slice(0, MAX_USER_RESULTS_PER_TYPE);
    }
  }, [users, debouncedSearch, participants, excludedUserIds]);

  const onSuccessfulClientModal = useCallback(
    (data?: Participant) => {
      onCloseModal();
      setSearch("");
      if (!data?.id) return;
      const source = data.userId ? data.user : data.client;
      const nameParts = (data.name || "").split(" ");
      const fallbackFirst = nameParts[0] || "";
      const fallbackLast = nameParts.slice(1).join(" ") || "";
      const firstname = source?.firstname || fallbackFirst;
      const lastname = source?.lastname || fallbackLast;

      onSelectParticipant({
        data: {
          id: data?.clientId,
          firstname,
          lastname,
          // Created clients carry their address in client.emailAddresses,
          // not in a top-level email field.
          email:
            data.email || (data.client ? getPrimaryEmail(data.client) : ""),
        } as Client,
        type: "client",
        participantId: data?.id,
      });
    },
    [onSelectParticipant, onCloseModal],
  );
  const { isContactSyncEnabled } = useCrm();

  useEffect(() => {
    if (!open) {
      setSearch("");
    }
  }, [open]);

  // Consider loading if:
  // - Users are still loading, OR
  // - Clients mutation is pending, OR
  // - We have a valid search but haven't fetched for it yet
  const hasFetchedCurrentSearch = lastFetchedSearch === debouncedSearch;
  const needsClientsFetch =
    debouncedSearch.length >= MIN_SEARCH_LENGTH && !hasFetchedCurrentSearch;
  const isFetching = isLoadingClients || isLoadingUsers || needsClientsFetch;

  // Add a delay after loading completes before showing results
  const [showResults, setShowResults] = React.useState(false);

  useEffect(() => {
    if (isFetching) {
      setShowResults(false);
    } else {
      const timer = setTimeout(() => {
        setShowResults(true);
      }, LOADING_TRANSITION_DELAY);
      return () => clearTimeout(timer);
    }
  }, [isFetching]);

  // Drops clients already in the conversation or shown as suggestions
  const filterOfferableClients = useCallback(
    (list: Client[] | undefined) => {
      if (!list || !Array.isArray(list)) return [];
      const participantsClientIds =
        participants?.map((p) => p.clientId).filter(Boolean) || [];
      const excludedClientIdSet = new Set(excludedClientIds);
      return list.filter(
        (client) =>
          !participantsClientIds.includes(client.id) &&
          !excludedClientIdSet.has(client.id),
      );
    },
    [participants, excludedClientIds],
  );

  const clientsList = useMemo(
    () => filterOfferableClients(clients),
    [filterOfferableClients, clients],
  );

  const defaultClientsList = useMemo(
    () => filterOfferableClients(defaultClients).slice(0, MAX_DEFAULT_CLIENTS),
    [filterOfferableClients, defaultClients],
  );

  const safeExistingParticipants = useMemo(
    () => (existingParticipants ?? []).filter((p) => p.userId || p.clientId),
    [existingParticipants],
  );

  const visibleExistingParticipants = useMemo(() => {
    if (isTypedNotEnough) return safeExistingParticipants;
    const q = debouncedSearch.toLowerCase();
    return safeExistingParticipants.filter((p) => {
      const name = getParticipantName(p).toLowerCase();
      const email = getParticipantEmail(p).toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [safeExistingParticipants, debouncedSearch, isTypedNotEnough]);

  const renderClientItem = (client: Client) => (
    <ConversationPersonTagSearchItem
      key={client.id}
      name={`${client.firstname} ${client.lastname}`}
      email={getPrimaryEmail(client)}
      crmType={client.crmContact?.crmId ? client.crmContact.crmType : undefined}
      handleSelectAuthor={() => {
        onSelectParticipant({
          data: client,
          type: "client",
          participantId: participant?.id,
          speakerId: participant?.speakerId,
        });
        onCloseModal();
      }}
      type="client"
      dataAttr={ANALYTICS_EVENTS.CONVERSATION_DETAILS_PARTICIPANT_ADDED}
      dataText="Assign client participant"
    />
  );

  const hasResults =
    clientsList.length > 0 ||
    filteredUsers.length > 0 ||
    visibleExistingParticipants.length > 0;

  return (
    <PopoverContent
      className={cn("p-0 text-left", popoverClassName)}
      side={side}
      align={align}
      avoidCollisions={avoidCollisions}
      forcePortal={forcePortal}
    >
      <Command shouldFilter={false}>
        <CommandInput
          placeholder={placeholder}
          onChangeCapture={(event) => {
            const val = (event.target as HTMLInputElement).value;
            setSearch(val);
          }}
        />
        <CommandList
          className={cn(
            !isTypedNotEnough &&
              (isFetching || !showResults) &&
              "min-h-[120px]",
          )}
        >
          <SuggestedParticipantsSection
            suggestedParticipants={suggestedParticipants}
            heading={suggestedHeading}
            participants={participants}
            users={users}
            participant={participant}
            onSelectParticipant={onSelectParticipant}
            onCloseModal={onCloseModal}
            maxVisible={suggestedMaxVisible}
          />

          {isTypedNotEnough && defaultClientsList.length > 0 && (
            <CommandGroup
              heading="Clients"
              data-cy="participant-default-clients"
            >
              {defaultClientsList.map(renderClientItem)}
            </CommandGroup>
          )}

          {isTypedNotEnough && isLoadingDefaultClients && (
            <div className="w-full flex justify-center py-4">
              <LoadingSpinner />
            </div>
          )}

          {isTypedNotEnough &&
            !isLoadingDefaultClients &&
            visibleExistingParticipants.length === 0 &&
            defaultClientsList.length === 0 && (
              <div
                className="flex flex-col items-center gap-1.5 px-4 py-6 text-center"
                data-cy="participant-search-empty"
              >
                <Icon
                  name="SearchIcon"
                  size="md"
                  className="text-typography-disabled"
                />
                <Text variant="p3" textColor="muted">
                  Type at least {MIN_SEARCH_LENGTH} characters to search{" "}
                  {disableUsers ? "clients" : "clients and advisors"}
                </Text>
              </div>
            )}

          {/* Existing conversation participants — always shown when they match the current query */}
          {visibleExistingParticipants.length > 0 && (
            <CommandGroup heading="In this conversation" className="pt-0">
              {visibleExistingParticipants.map((p) => {
                const payload = toUpdateParticipantData(p, participant);
                return (
                  <ConversationPersonTagSearchItem
                    key={p.id}
                    name={getParticipantName(p)}
                    email={getParticipantEmail(p)}
                    type={payload.type}
                    crmType={
                      payload.type === "client" && p.client?.crmContact?.crmId
                        ? p.client.crmContact.crmType
                        : undefined
                    }
                    handleSelectAuthor={() => {
                      onSelectParticipant(payload);
                      onCloseModal();
                    }}
                    onUnassign={
                      p.speakerId === participant?.speakerId
                        ? onUnassign
                        : undefined
                    }
                  />
                );
              })}
            </CommandGroup>
          )}

          {!isTypedNotEnough && (isFetching || !showResults) && (
            <div className="w-full flex justify-center py-4">
              <LoadingSpinner />
            </div>
          )}

          {showResults &&
            debouncedSearch.length >= MIN_SEARCH_LENGTH &&
            !hasResults && (
              <div className="ml-4 text-center text-xs py-2">
                <CommandEmpty>No participants found.</CommandEmpty>
              </div>
            )}

          {!isTypedNotEnough &&
            showResults &&
            !isLoadingClients &&
            !isLoadingUsers &&
            hasResults && (
              <>
                {/* Render Clients */}
                {clientsList.length > 0 && (
                  <CommandGroup heading="Clients">
                    {clientsList.map(renderClientItem)}
                  </CommandGroup>
                )}

                {!disableUsers && (
                  <>
                    {/* Render Users */}
                    {filteredUsers.length > 0 && (
                      <CommandGroup heading="Advisors & assistants">
                        {filteredUsers.map((user) => (
                          <ConversationPersonTagSearchItem
                            key={user.id}
                            name={`${user.firstname} ${user.lastname}`}
                            email={user.email}
                            handleSelectAuthor={() => {
                              onSelectParticipant({
                                data: user,
                                type: "user",
                                participantId: participant?.id,
                                speakerId: participant?.speakerId,
                              });
                              onCloseModal();
                            }}
                            type="user"
                            dataAttr={
                              ANALYTICS_EVENTS.CONVERSATION_DETAILS_PARTICIPANT_ADDED
                            }
                            dataText="Assign advisor participant"
                          />
                        ))}
                      </CommandGroup>
                    )}
                  </>
                )}
              </>
            )}
        </CommandList>
        {!disableUsers && (
          <div className="border-t border-border-light p-1 bg-popover">
            <ClientModal
              conversationId={conversationId}
              speakerId={participant?.speakerId}
              defaultValues={{
                firstname:
                  (participant?.userId
                    ? participant?.user?.firstname
                    : participant?.client?.firstname) ||
                  participant?.name?.split(" ")[0] ||
                  search.split(" ")[0] ||
                  "",
                lastname:
                  (participant?.userId
                    ? participant?.user?.lastname
                    : participant?.client?.lastname) ||
                  participant?.name?.split(" ").slice(1).join(" ") ||
                  search.split(" ").slice(1).join(" ") ||
                  "",
                // getParticipantEmail, not participant.email: for legacy-flow
                // clients the participant row carries no email and the address
                // lives on the linked client. Matches every other consumer here.
                email: getParticipantEmail(participant),
              }}
              onSuccess={onSuccessfulClientModal}
              participantId={participant?.id}
            >
              {isContactSyncEnabled ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0}>
                      <Button
                        size="sm"
                        variant="ghost"
                        iconLeft={<Icon name="AddIcon" />}
                        disabled
                      >
                        {createClientLabel}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    {`If contact sync is on, you can't create clients in Focal. Please create clients directly in your CRM instead.`}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  iconLeft={<Icon name="AddIcon" />}
                  onClick={() => {
                    track(
                      ANALYTICS_EVENTS.CONVERSATION_DETAILS_PARTICIPANT_CREATED,
                      { event_text: "Add a new client" },
                    );
                  }}
                >
                  {createClientLabel}
                </Button>
              )}
            </ClientModal>
          </div>
        )}
      </Command>
    </PopoverContent>
  );
};
